import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Phone, Clock, MapPin, Calendar, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const ReservationPage = () => {
  const { t } = useTranslation();

  // ZenChef booking URL
  const zenchefUrl = "https://bookings.zenchef.com/results?rid=376477";

  return (
    <div className="pt-20" data-testid="reservation-page">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="/images/plats/apero.jpeg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-gold font-accent text-lg tracking-widest">
              {t("reservation.subtitle")}
            </span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl mt-2 mb-6">
              {t("reservation.title")}
            </h1>
            <div className="section-divider mx-auto bg-gradient-to-r from-transparent via-gold to-transparent" />
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left - ZenChef Placeholder */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="border-border/40 shadow-lg overflow-hidden">
                <CardContent className="p-0">
                  {/* ZenChef Integration Placeholder */}
                  <div
                    id="zenchef-widget"
                    className="bg-secondary min-h-[500px] flex flex-col items-center justify-center p-8"
                    data-testid="zenchef-widget-container"
                  >
                    <div className="text-center">
                      <Calendar className="h-16 w-16 text-gold mx-auto mb-6" />
                      <h3 className="font-heading text-2xl text-primary mb-4">
                        {t("reservation.online")}
                      </h3>
                      <p className="text-muted-foreground mb-8 max-w-sm font-body">
                        {t("reservation.description")}
                      </p>

                      {/* ZenChef Button - Opens in new tab until widget is integrated */}
                      <a
                        href={zenchefUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button
                          size="lg"
                          className="bg-gold hover:bg-gold/90 text-white rounded-none px-10 py-7 text-sm tracking-wider uppercase btn-hover-expand gap-2"
                          data-testid="zenchef-reserve-btn"
                        >
                          <Calendar className="h-5 w-5" />
                          {t("hero.cta_reserve")}
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </a>

                      <p className="text-xs text-muted-foreground mt-6">
                        {t("reservation.powered_by")}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Note about ZenChef integration */}
              <div className="mt-4 p-4 bg-muted/50 rounded text-sm text-muted-foreground">
                <p className="font-body">
                  <strong>{t("common.note_label")}</strong> {t("reservation.integration_note")}
                </p>
              </div>
            </motion.div>

            {/* Right - Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {/* Phone */}
              <Card className="border-border/40 card-elegant">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-gold" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl text-primary mb-2">
                        {t("reservation.phone")}
                      </h3>
                      <a
                        href="tel:+41223424243"
                        className="text-2xl font-heading text-gold hover:underline"
                        data-testid="phone-link"
                      >
                        {t("common.phone_display")}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Hours */}
              <Card className="border-border/40 card-elegant">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="h-6 w-6 text-gold" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl text-primary mb-4">
                        {t("reservation.hours_title")}
                      </h3>
                      <div className="space-y-2 text-muted-foreground font-body">
                        <p>{t("reservation.hours_weekday")}</p>
                        <p>{t("reservation.hours_saturday")}</p>
                        <p>{t("reservation.hours_sunday")}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Address */}
              <Card className="border-border/40 card-elegant">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-gold" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl text-primary mb-2">
                        {t("contact.address_title")}
                      </h3>
                      <p className="text-muted-foreground font-body">
                        {t("contact.address_line1")}<br />
                        {t("contact.address_line2")}<br />
                        {t("contact.address_line3")}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Additional Info */}
              <div className="p-6 bg-primary/5 border border-primary/10 rounded">
                <h4 className="font-heading text-lg text-primary mb-3">
                  {t("reservation.important_info_title")}
                </h4>
                <ul className="space-y-2 text-muted-foreground text-sm font-body">
                  <li>• {t("reservation.info_dine_in_only")}</li>
                  <li>• {t("reservation.info_no_takeaway")}</li>
                  <li>• {t("reservation.info_no_delivery")}</li>
                  <li>• {t("reservation.info_groups")}</li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Preview */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="font-heading text-3xl text-primary mb-4">
              {t("contact.find_us_title")}
            </h2>
            <p className="text-muted-foreground font-body">
              {t("contact.find_us_description")}
            </p>
          </motion.div>

          <div className="aspect-video overflow-hidden rounded shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2762.8654!2d6.1390!3d46.1831!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478c7ad45a26e41f%3A0x8b2e7b4c5c5e5e5e!2sRue%20des%20Noirettes%2032%2C%201227%20Carouge%2C%20Switzerland!5e0!3m2!1sfr!2sch!4v1699999999999!5m2!1sfr!2sch"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={t("contact.map_title")}
              data-testid="google-maps-iframe"
            />
          </div>

          <div className="text-center mt-6">
            <a
              href="https://www.google.com/maps/dir//Rue+des+Noirettes+32,+1227+Carouge,+Switzerland"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-white rounded-none px-6 py-5 gap-2"
                data-testid="directions-btn"
              >
                <MapPin className="h-4 w-4" />
                {t("contact.directions")}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReservationPage;
